// react
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// utils
import * as http from "../../utils/http";
import { translate } from '../../utils/translator';
import { areTimestampsEqual } from '../../utils/timestampFormatter';

// components
import HeaderController from '../../components/Headers/HeaderController';
import ScheduleAppointmentView from './ScheduleAppointmentView';
import ChooseDoctorView from './ChooseDoctorView';

// types
import { DoctorAvailabilityData } from 'data-types';

// constants
import { GRID_FULL_WIDTH, PAGE_TRANSITION_DURATION, PAGE_TRANSITION_DELAY, GAP_BETWEEN_VIEWS } from './consts';
import { useAppContext } from '../../providers/AppProvider';
import { loadCustomerFromSession } from '../../customHooks/useCustomer';
import { useVideoConsultationAppointment } from '../../customHooks/useVideoConsultationAppointment';
import { CHOOSE_DOCTOR_PAGE_VIEW_EVENT } from '../../utils/consts';

// trackers
import * as SocialMediaTracker from "../../PixelTrackers/SocialMediaTracker";

type TimekitBooking = {
	id: number;
	cart_id: number;
	start: string;
	end: string;
	created_at: string;
	updated_at: string;
	selectedDoctor: DoctorAvailabilityData;
} | undefined;


const ChooseDoctorAndAppointment: React.FC = () => {
	const { country = 'de', language = 'de', mention } = useParams();
	const [searchParams] = useSearchParams();
	const [skipToAppointmentSummary, setSkipToAppointmentSummary] = useState(false);

	const {
		selectedDoctor,
		setSelectedDoctor,
		selectedDate,
		setSelectedDate,
		selectedTime,
		setSelectedTime,
		handleDateSelection,
		handleSelectDoctor: hookHandleSelectDoctor,
		doctorsAvailabilityData,
		setDoctorsAvailabilityData,
		setIsSubmitting,
		allowSubmit,
	} = useVideoConsultationAppointment();
	
	const {
		isLoading,
		setIsLoading
	} = useAppContext();
	const [showSchedulingView, setShowSchedulingView] = useState(false);
	const [flowSliderStep, setFlowSliderStep] = useState<number>(-1);
	const [hasAvailableDoctors, setHasAvailableDoctors] = useState<boolean>(true);
	const [backButtonText, setBackButtonText] = useState<string>(translate(language, 'choose_doctor', hasAvailableDoctors ? 'schedule_appointment/back_to_doctors_with_doctors' : 'schedule_appointment/back_to_doctors_without_doctors' ));
	
	const navigate = useNavigate();

	const noAvailableDoctorsFallback = () => {
		setShowSchedulingView(true);
		setFlowSliderStep(0);
		document.body.style.overflowY = 'hidden';
		// Back button should go to choose treatment when no doctors available
		setBackButtonText(translate(language, 'choose_doctor', 'schedule_appointment/back_to_choose_treatment') || translate(language, 'default', 'back_to_choose_treatment') || 'Back to choose treatment');
	}


	// primary effect: get data from server
	useEffect(() => {
		// Mark this page in history so back button works correctly
		window.history.replaceState({ page: 'choose-doctor' }, '', window.location.href);

		// handle back button
		const handlePopState = () => {
			navigate(
				{pathname: `/select_treatment/${country}/${language}/${mention}`, search: searchParams.toString()},
				{ replace: true }
			);
		}
		window.addEventListener("popstate", handlePopState);

		const originalOverflowY = document.body.style.overflowY;

		// Fetch all available slots for all doctors
		http.getFromServer(
			`choose-doctor/${country}/${language}?countryID=${country}`,
			[http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER] 
		)
		.then((response) => {
			// Page View event
			SocialMediaTracker.track("pv", CHOOSE_DOCTOR_PAGE_VIEW_EVENT, {searchParamas: Object.fromEntries(searchParams.entries())});

			const doctorsAvailabilityData = response.data.doctorsAvailabilityData as DoctorAvailabilityData[];
			const hasAvailableDoctorsFlag = response.data.hasAvailableDoctors !== false; // Default to true if not provided
			setHasAvailableDoctors(hasAvailableDoctorsFlag);

			if (response.data && response.data.success) {
				const { timekitBooking } = response.data as { timekitBooking: TimekitBooking};

				// Only auto-load timekitBooking if it has a selectedDoctor (not for preferred time bookings)
				// if the customer has an existing appointment, show the scheduling view with the existing appointment details
				if (timekitBooking && timekitBooking.selectedDoctor) {
					// Find the doctor's availability and then the specific date
					const doctorAvailability = doctorsAvailabilityData.find(da => da.doctorID === timekitBooking.selectedDoctor.doctorID);
					const dateAvailability = doctorAvailability?.availability.find(dateAvail => dateAvail.date === timekitBooking.start.split('T')[0]);
					
					// Find the matching timeslot from the timekit data to get the correct format
					let matchingTimeslot: any = null;
					if (dateAvailability) { // If it wasn't found, we have a problem, it shouldn't happen
						matchingTimeslot = dateAvailability.timeslots.find(slot => 
							areTimestampsEqual(slot.start, timekitBooking.start)
						);
					}
					
					// set the customers existing appointment to show up in the scheduling view
					setSelectedDoctor(timekitBooking.selectedDoctor);
					setSelectedDate(dateAvailability ?? null); 
					// Use the timekit format if found, otherwise fallback to server format
					setSelectedTime(matchingTimeslot || {start: timekitBooking.start, end: timekitBooking.end});
					setShowSchedulingView(true);
					setFlowSliderStep(2); // to confirm the appointment
					setSkipToAppointmentSummary(true); // to skip to the appointment summary step

					setTimeout(() => {
						setSkipToAppointmentSummary(false);
					}, PAGE_TRANSITION_DURATION + PAGE_TRANSITION_DELAY + 200); // +200ms to ensure the animation is complete
					document.body.style.overflowY = 'hidden'; // prevent body scroll on scheduling view
					setBackButtonText(translate(language, 'choose_doctor', 'schedule_appointment/back_to_time'));
				} else if (!hasAvailableDoctorsFlag) {
					// No doctors available - skip to scheduling view for preferred time selection
					noAvailableDoctorsFallback();
				}

				setDoctorsAvailabilityData(doctorsAvailabilityData);
			} else {
				noAvailableDoctorsFallback();
			}
		})
		.catch((err: any) => {
			window.clientLogger.error("error fetching availability data:", err);
			noAvailableDoctorsFallback();
		})
		.finally(() => {
			setIsLoading(false);
		});

		return () => {
			// reinstate the original overflowY
			document.body.style.overflowY = originalOverflowY; // restore body scroll
			window.removeEventListener("popstate", handlePopState);
		}
	}, [country]);


	// handlers
	// 1. Main submit handler - appointment time to cartTimekitbookings
	const handleSubmit = () => {
		if (!allowSubmit) {
			return;
		}

		setIsSubmitting(true);

		http.postToServer(`choose-doctor/${country}/${language}`, {		
				doctor_id: selectedDoctor?.doctorID ?? null,
				start: selectedTime!.start,
				end: selectedTime!.end,
			}, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
		.then((response) => {
						
			const customerFromSession = loadCustomerFromSession();    
			const updatedSearchParams = new URLSearchParams(searchParams); 
			const deeplinkCoupon = window.eventsTracking.getData("cpn")

			if (deeplinkCoupon) {
			  updatedSearchParams.set("cpn", deeplinkCoupon);
			}
			
			if (!customerFromSession) {
			  // if no customer id send to registration
			  navigate({ pathname: `/sign_up/${country}/${language}`, search: updatedSearchParams.toString() });
			} else {
				navigate({ pathname: `/shipping/${country}/${language}`, search: updatedSearchParams.toString() });
			}

		}).catch((err) => {
			// TODO: add sticky notification
			window.clientLogger.error('Error submitting appointment time',err);
		})
		.finally(() => {
			setIsSubmitting(false);
		});
	}

	// 2. handle selected doctor wrapper function
	const handleSelectDoctor = (doctorAvailabilityData: DoctorAvailabilityData) => {
		hookHandleSelectDoctor(doctorAvailabilityData);

		// Trigger animation to show scheduling view
		setShowSchedulingView(true);
		setFlowSliderStep(0);
		document.body.style.overflow = 'hidden'; // prevent body scroll on scheduling view

	}

	const handleBackToDoctors = () => {
		// If no doctors available, go back to previous page instead of showing doctor selection
		if (!hasAvailableDoctors) {
			navigate(
				{pathname: `/select_treatment/${country}/${language}/${mention}`, search: searchParams.toString()},
				{ replace: true }
			);
			return;
		}
		setShowSchedulingView(false);
		document.body.style.overflow = 'unset'; // restore body scroll
	}
	

	return (
		<>
			{/* scheduling view */}
			<ScheduleAppointmentView 
				language={language}
				country={country}
				handleBackToDoctors={handleBackToDoctors}
				handleDateSelection={handleDateSelection}
				handleSubmit={handleSubmit}
				selectedDoctor={selectedDoctor}
				setSelectedTime={setSelectedTime}
				selectedDate={selectedDate}
				selectedTime={selectedTime}
				showSchedulingView={showSchedulingView}
				flowSliderStep={flowSliderStep}
				setFlowSliderStep={setFlowSliderStep}
				backButtonText={backButtonText}
				setBackButtonText={setBackButtonText}
				isLoading={isLoading}
				allowSubmit={allowSubmit}
				skipToAppointmentSummary={skipToAppointmentSummary}
				layoutWithDoctors={hasAvailableDoctors}
			/>

			<div 
				className="
					min-h-screen 
					grid 
					grid-rows-[auto_1fr_auto]
					md:grid-cols-[minmax(30px,1fr)_minmax(0,1200px)_minmax(30px,1fr)]
					grid-cols-[minmax(15px,1fr)_minmax(0,1200px)_minmax(15px,1fr)]
					bg-[#F5F7F5]
				"
			>
				<div className={`${GRID_FULL_WIDTH} z-10 sticky top-0 pin-below-app-bar`}>
					<HeaderController showSteps={true} stage={"chooseDoctorAndAppointment"} isCannabis={mention?.includes("medical_cannabis")} />
				</div>
			
				{/* main */}
				<main 
					id="choose-doctor-and-appointment-container-main"
					className="
						pt-4 md:pt-10
						col-start-2
						overflow-x-hidden
					"
				>
					<div
						id="choose-doctor-and-appointment-container-transition-container"
						className="
							grid
						"
						style={{
							gridTemplateColumns: `100% ${GAP_BETWEEN_VIEWS}px 100%`,
							transform: showSchedulingView ? `translateX(calc(-100% - ${GAP_BETWEEN_VIEWS}px))` : 'translateX(0)',
							transition: `transform ${PAGE_TRANSITION_DURATION}ms ease-in-out ${PAGE_TRANSITION_DELAY}ms`
						}}
					>
						{/* choose doctor view */}
						<ChooseDoctorView 
							layoutWithDoctors={hasAvailableDoctors}
							language={language}
							doctorsAvailabilityData={doctorsAvailabilityData}
							handleSelectDoctor={handleSelectDoctor}
							isLoading={isLoading}
						/>
					
					</div>
				</main>
 
				<footer 
					id="choose-doctor-and-appointment-container-footer"
					className="
						mt-5
						col-start-2
						p-4
						border-t-[2px]
						[border-top-style:solid]
						border-[#EEF1F6]
					"
				>
				</footer>
			</div>
		</>
	);
}

export default ChooseDoctorAndAppointment;
